import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Api from "services/api";
import toast from "react-hot-toast";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { userSelector } from "slices/userSlice";
import { usePermissions } from "layouts/Admin";// ✅ Interpreter layout
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import ErrorMessage from "components/common/ErrorMessage";
import { fetchInterpreterTimesheets } from "slices/interpreter/interpreterTimesheetSlice"; // ✅ create separate slice for interpreter
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";

const apiService = new Api();

function InterpreterTimesheet() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { checkPermission } = usePermissions();
    const { user } = useSelector(userSelector);
    const userToken = user?.token;
    const [hasFiltered, setHasFiltered] = useState(false);
    const [paginationParams, setPaginationParams] = useState({});
    const filtersRef = useRef({});

    const {
        timesheets,
        loader,
        total,
        last_page: totalPages,
        current_page,
        per_page: pageLength,
    } = useSelector((state) => state.interpretertimesheet || {});

    console.log("🔍 InterpreterTimesheet state snapshot:", {
        timesheets,
        loader,
        total,
        totalPages,
        current_page,
        pageLength,
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({
        sortBy: "date",
        sortDirection: "desc",
    });

    // Filters state
    const defaultValues = {
        start_date: "",
        end_date: "",
    };

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({ defaultValues });

    //#region FILTER HANDLERS
    const handleResetFields = () => {
        reset(defaultValues);
        setHasFiltered(false);
        setPaginationParams({});
        filtersRef.current = {};
        dispatch(fetchInterpreterTimesheets(1, defaultValues));
    };

    const onSubmit = (data) => {
        setHasFiltered(true);
        setPaginationParams(data);
        filtersRef.current = data;
        dispatch(fetchInterpreterTimesheets(1, data));
    };
    //#endregion

    //#region TABLE HEADER
    const renderTableHeader = useCallback(() => {
        const callBack = (dir, sortBy) => {
            setSortConfig({ sortBy, sortDirection: dir });
            dispatch(
                fetchInterpreterTimesheets(currentPage, {
                    sortBy,
                    sortDirection: dir,
                    search: searchTerm,
                })
            );
        };

        return (
            <tr>
                <th className="col-1">#</th>
                <th className="col-2">
                    <TableHeaderItem
                        title={t("timesheet.date")}
                        sortBy="date"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th className="col">{t("timesheet.checkInOut")}</th>
                <th className="col-2">{t("timesheet.dailyDuration")}</th>
            </tr>
        );
    }, [sortConfig, currentPage, searchTerm]);
    //#endregion

    //#region EXCEL EXPORT
    const handleDownloadTimesheet = async () => {
        try {
            // Step 1️⃣: Setup query params
            const params = {
                ...paginationParams, // includes start_date, end_date etc.
                sortBy: sortConfig?.sortBy,
                sortDirection: sortConfig?.sortDirection,
                search: searchTerm,
            };

            console.log("🚦 Export (Interpreter) - fetching all pages with params:", params);

            let page = 1;
            let allData = [];
            let lastPage = 1;

            // Step 2️⃣: Fetch all pages from backend
            do {
                const res = await Api.fetchInterpreterTimesheets(page, params); // 👈 use correct API
                const response = res?.data;

                if (!response || !response.data) break;

                allData = [...allData, ...response.data];
                lastPage = response.pagination?.last_page || 1;

                console.log(`✅ Interpreter Page ${page} fetched: ${response.data.length} items`);
                page++;
            } while (page <= lastPage);

            console.log("🚦 All interpreter pages combined:", allData.length);

            if (allData.length === 0) {
                toast.error(t("timesheet.noDataAvailable"));
                return;
            }

            // Step 3️⃣: Create Excel Workbook
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Timesheet");

            // ✅ Interpreter Name Row
            const interpreterName = allData[0]?.interpreter_name || "Interpreter";
            const title = `Timesheet for ${interpreterName}`;
            const titleRow = worksheet.addRow([title]);
            worksheet.mergeCells("A1:D1");

            titleRow.getCell(1).font = { name: "Calibri", size: 14, bold: true, color: { argb: "6C889C" } };
            titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
            titleRow.height = 25;

            // ✅ Header
            const headers = ["#", "Date", "Check In/Out", "Daily Duration"];
            const headerRow = worksheet.addRow(headers);
            headerRow.eachCell((cell) => {
                cell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "6F8DFF" },
                };
                cell.alignment = { horizontal: "center", vertical: "middle" };
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            });
            worksheet.views = [{ state: "frozen", ySplit: 2 }];
            headerRow.height = 20;

            // ✅ Data rows
            allData.forEach((item, index) => {
                worksheet.addRow([
                    index + 1,
                    moment(item.date).format("YYYY-MM-DD"),
                    item.pairs || "N/A",
                    item.daily_duration || "0h",
                ]);
            });

            // ✅ Auto column width
            worksheet.columns.forEach((col, index) => {
                if (index === 2) {
                    col.width = 100;
                } else {
                    let max = 0;
                    col.eachCell({ includeEmpty: true }, (cell) => {
                        const val = cell.value ? cell.value.toString() : "";
                        max = Math.max(max, val.length);
                    });
                    col.width = max + 3;
                }
            });

            // ✅ Total duration
            let totalMinutes = 0;
            allData.forEach((item) => {
                if (item.daily_duration) {
                    const str = item.daily_duration.toString().trim().toLowerCase();
                    let hours = 0,
                        minutes = 0;

                    if (str.includes("h")) {
                        const hMatch = str.match(/(\d+)\s*h/);
                        const mMatch = str.match(/(\d+)\s*m/);
                        hours = hMatch ? parseInt(hMatch[1]) : 0;
                        minutes = mMatch ? parseInt(mMatch[1]) : 0;
                    } else if (str.includes(":")) {
                        const [h, m] = str.split(":");
                        hours = parseInt(h);
                        minutes = parseInt(m);
                    }
                    totalMinutes += hours * 60 + minutes;
                }
            });

            const totalHours = Math.floor(totalMinutes / 60);
            const remainingMinutes = totalMinutes % 60;
            const totalFormatted = `${totalHours}h ${remainingMinutes}m`;
            const totalDays = allData.length;
            const daysRow = worksheet.addRow(["", "", "Total Days", totalDays]);
            const totalRow = worksheet.addRow(["", "", "Total Duration:", totalFormatted]);
            [daysRow, totalRow].forEach((row) => {
                row.eachCell((cell, colNumber) => {
                    cell.font = { bold: true };
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };
                    if (colNumber === 3) cell.alignment = { horizontal: "right" };
                    if (colNumber === 4)
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "D9E1F2" },
                        };
                });
            });

            // ✅ Save file
            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(
                new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                }),
                `timesheet_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
            );

            toast.success(t("timesheet.downloadSuccess"));
        } catch (err) {
            console.error("Export error:", err);
            toast.error(t("timesheet.downloadError"));
        }
    };
    //#endregion

    //#region TABLE BODY
    const renderTableContent = useCallback(() => {
        if (!hasFiltered || !timesheets?.length) return <></>;

        return timesheets.map((item, index) => (
            <TimesheetItems
                key={item.id}
                data={item}
                index={index}
                current_page={current_page}
                pageLength={pageLength}
            />
        ));
    }, [timesheets, hasFiltered]);
    //#endregion
    console.log("🧩 Current paginationParams:", paginationParams);
    return (
        <>
            {/* ---------------- FILTER FORM ---------------- */}
            <div className="content" style={{ minHeight: "auto" }}>
                <div className="row_main_head">
                    <h2 className="main_heading">{t("sidebar.timesheet")}</h2>
                </div>
                <div className="card_table">
                    <form className="invoicing_form" onSubmit={handleSubmit(onSubmit)}>
                        <div className="billings row_Invoice">
                            {/* From Date */}
                            <div className="row-primary">
                                <h2>{t("timesheet.fromDateRequired")}</h2>
                                <Controller
                                    name="start_date"
                                    rules={{ required: "This field is required" }}
                                    control={control}
                                    render={({ field }) => (
                                        <div className="input-image-wrapper d-flex flex-column w-100 align-items-start">
                                            <div className="input-image">
                                                <input
                                                    type="date"
                                                    {...field}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            moment(new Date(e.target.value)).format("YYYY-MM-DD")
                                                        )
                                                    }
                                                />
                                                <img
                                                    src="/images/celender.png"
                                                    alt="calendar Icon"
                                                    className="custom-icon"
                                                />
                                            </div>
                                            {errors?.start_date?.message && (
                                                <div style={{ marginBottom: 10 }}>
                                                    <ErrorMessage text={errors?.start_date?.message} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>

                            {/* To Date */}
                            <div className="row-primary">
                                <h2>{t("timesheet.toDateRequired")}</h2>
                                <Controller
                                    name="end_date"
                                    rules={{ required: "This field is required" }}
                                    control={control}
                                    render={({ field }) => (
                                        <div className="input-image-wrapper d-flex flex-column w-100 align-items-start">
                                            <div className="input-image">
                                                <input
                                                    type="date"
                                                    {...field}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            moment(new Date(e.target.value)).format("YYYY-MM-DD")
                                                        )
                                                    }
                                                />
                                                <img
                                                    src="/images/celender.png"
                                                    alt="calendar Icon"
                                                    className="custom-icon"
                                                />
                                            </div>
                                            {errors?.end_date?.message && (
                                                <div style={{ marginBottom: 10 }}>
                                                    <ErrorMessage text={errors?.end_date?.message} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="row-primary row_10 mb-0 p-3">
                            <SubmitButton
                                handleSubmit={handleSubmit(onSubmit)}
                                btnText={t("dashboard.search")}
                                className={"green"}
                            />
                            <SubmitButton
                                buttonType="button"
                                className={"lightBlue"}
                                btnText={t("dashboard.reset")}
                                handleSubmit={handleResetFields}
                            />
                        </div>
                    </form>
                </div>
            </div>

            {/* ---------------- TABLE SECTION ---------------- */}
            <ListLayout
                buttonTitle={t("timesheet.download")}
                renderTableHeader={renderTableHeader}
                renderTableContent={renderTableContent}
                totalPages={totalPages}
                fetchList={(page, extraParams = {}, disableLoader) => {
                    const currentFilters = {
                        start_date: filtersRef.current.start_date || "",
                        end_date: filtersRef.current.end_date || "",
                    };

                    const params = {
                        ...extraParams,
                        ...currentFilters,
                    };

                    console.log("Final Params Sent:", params);
                    return fetchInterpreterTimesheets(page, params, disableLoader);
                }}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                loader={loader}
                noOfItems={total}
                containerStyle={{ marginTop: "0" }}
                handleAddPopUp={handleDownloadTimesheet}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchPlaceholder="Search Date..."
                disableBootstrap={true}
                paginationParams={paginationParams}
                defaultPageLength={pageLength}
                sortConfig={sortConfig}
            />
        </>
    );
}

// ✅ Table Row
const TimesheetItems = ({ data, index, current_page, pageLength }) => {
    const serial = useMemo(
        () => index + 1 + (current_page - 1) * pageLength,
        [current_page, pageLength]
    );
    return (
        <tr>
            <td>{serial}</td>
            <td>{moment(data?.date).format("YYYY-MM-DD")}</td>
            <td
                style={{
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    maxWidth: "100px",
                }}
            >
                {data?.pairs || "N/A"}
            </td>
            <td>{data?.daily_duration || "0h"}</td>
        </tr>
    );
};

export default InterpreterTimesheet;
